import { create } from "zustand";

export interface JobDescriptionFormData {
  // Basic fields
  prompt: string;
  useDetailedForm: boolean;

  // Job Information
  companyName: string;
  jobTitle: string;
  department: string;
  customDepartment: string;
  employmentType: string;
  experienceLevel: string;
  location: string;

  // Additional Information
  keySkills: string;
  salaryRange: string;
  benefits: string;

  // Files
  selectedFiles: Array<{
    name: string;
    size?: number;
    type?: string;
    isKnowledgeFile?: boolean;
    database?: string;
  }>;
}

interface JobDescriptionFormStore {
  formData: JobDescriptionFormData;
  updateField: <K extends keyof JobDescriptionFormData>(
    field: K,
    value: JobDescriptionFormData[K]
  ) => void;
  updateFormData: (data: Partial<JobDescriptionFormData>) => void;
  resetForm: () => void;
  isFormValid: () => boolean;
  isDetailedFormValid: () => boolean;
  isSimpleFormValid: () => boolean;
  getInputPrompt: () => string;
}

const initialFormData: JobDescriptionFormData = {
  prompt: "",
  useDetailedForm: false,
  companyName: "",
  jobTitle: "",
  department: "",
  customDepartment: "",
  employmentType: "",
  experienceLevel: "",
  location: "",
  keySkills: "",
  salaryRange: "",
  benefits: "",
  selectedFiles: [],
};

export const useJobDescriptionFormStore = create<JobDescriptionFormStore>(
  (set, get) => ({
    formData: initialFormData,

    updateField: (field, value) =>
      set((state) => ({
        formData: { ...state.formData, [field]: value },
      })),

    updateFormData: (data) =>
      set((state) => ({
        formData: { ...state.formData, ...data },
      })),

    resetForm: () =>
      set({
        formData: initialFormData,
      }),

    isDetailedFormValid: () => {
      const { formData } = get();
      const dept =
        formData.department === "Other"
          ? formData.customDepartment.trim()
          : formData.department.trim();
      return !!(
        formData.companyName.trim() &&
        formData.jobTitle.trim() &&
        dept &&
        formData.employmentType.trim() &&
        formData.experienceLevel.trim() &&
        formData.location.trim()
      );
    },

    isSimpleFormValid: () => {
      const { formData } = get();
      return formData.prompt.trim() !== "";
    },

    isFormValid: () => {
      const { formData, isDetailedFormValid, isSimpleFormValid } = get();
      return formData.useDetailedForm
        ? isDetailedFormValid()
        : isSimpleFormValid();
    },

    getInputPrompt: () => {
      const { formData } = get();
      if (formData.useDetailedForm) {
        const dept =
          formData.department === "Other"
            ? formData.customDepartment
            : formData.department;
        const metaData = {
          companyName: formData.companyName,
          jobTitle: formData.jobTitle,
          department: dept,
          employmentType: formData.employmentType,
          experienceLevel: formData.experienceLevel,
          location: formData.location,
          keySkills: formData.keySkills,
          salaryRange: formData.salaryRange,
          benefits: formData.benefits,
          prompt: formData.prompt,
        };
        return JSON.stringify(metaData);
      }
      return formData.prompt;
    },
  })
);
